import { FC, useMemo, useCallback, useEffect } from 'react';
import { css, Theme, useTheme } from '@emotion/react';
import { darken } from 'polished';
import { WarningCircle, ArrowClockwise, Plus, Link, GithubLogo } from '@phosphor-icons/react';
import { useGitHubState } from 'client/state/GitHubState';
import { Button } from 'client/components/Button';
import { Dialog } from 'client/components/Dialog/Dialog';
import { useGitHubAccess } from 'client/components/GitHub/useGitHubAccess';
import { Help } from 'client/components/Help';
import { Typography } from 'client/components/Typography';
import { borderRadius } from 'client/Theme';

const contentStyle = (theme: Theme) => {
  const bgSvgColor = darken(0.03, theme.color.grey[60]).replace('#', '%23');
  return css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 6px;
    padding: 2rem;
    background-color: ${theme.color.grey[60]};
    border-radius: ${borderRadius}px;

    background-size: 100%;
    background-repeat: no-repeat;
    background-position: -90px -40px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' fill='${bgSvgColor}' viewBox='0 0 256 256'%3E%3Crect width='256' height='256' fill='none'%3E%3C/rect%3E%3Ccircle cx='68' cy='188' r='28' fill='none' stroke='${bgSvgColor}' stroke-linecap='round' stroke-linejoin='round' stroke-width='16'%3E%3C/circle%3E%3Ccircle cx='68' cy='68' r='28' fill='none' stroke='${bgSvgColor}' stroke-linecap='round' stroke-linejoin='round' stroke-width='16'%3E%3C/circle%3E%3Ccircle cx='188.00244' cy='140' r='28' fill='none' stroke='${bgSvgColor}' stroke-linecap='round' stroke-linejoin='round' stroke-width='16'%3E%3C/circle%3E%3Cpath d='M160.00244,144H130.482a48,48,0,0,1-36.87462-17.27115L68,96v64' fill='none' stroke='${bgSvgColor}' stroke-linecap='round' stroke-linejoin='round' stroke-width='16'%3E%3C/path%3E%3C/svg%3E");

    > svg {
      margin-top: 1.5rem;
      margin-bottom: 1rem;
    }

    > .error {
      position: absolute;
      margin-top: 70px;
      margin-left: 50px;
      background-color: ${theme.color.white};
      border-radius: 50%;
    }

    > p {
      max-width: 50ch;
      text-align: center;
    }

    > button {
      margin-top: 1.8rem;
      margin-bottom: 0.2rem;
    }
  `;
};

const unauthenticatedTitle = 'Connect with GitHub';
const unauthenticatedDescription = 'You have not yet connected your Diploi account with GitHub.';
const unauthenticatedButton = 'Connect GitHub Account';

const errorRepositoryListTitle = 'Failed to Connect to GitHub';
const errorRepositoryListDescription =
  "Something wen't wrong while trying to retrieve your information from GitHub. Reconnecting might fix the issue.";
const errorRepositoryListButton = 'Reconnect GitHub Account';

const getContent = (state: string) => {
  switch (state) {
    case 'error':
      return {
        title: errorRepositoryListTitle,
        description: errorRepositoryListDescription,
        button: errorRepositoryListButton,
      };
    default:
      return {
        title: unauthenticatedTitle,
        description: unauthenticatedDescription,
        button: unauthenticatedButton,
      };
  }
};

export const AuthenticateGithubDialog: FC = () => {
  const theme = useTheme();
  const isOpen = useGitHubState((state) => state.isDialogOpen);
  const { user, authenticate, isUnauthenticated, isLoading, isAuthenticating, isError } = useGitHubAccess({
    scope: ['user'],
  });

  const { title, description, button } = useMemo(() => getContent(isError ? 'error' : 'missing'), [isError]);

  const handleClose = useCallback(() => useGitHubState.setState({ isDialogOpen: false }), []);

  useEffect(() => {
    useGitHubState.getState().reload();
    handleClose();
  }, [user, isUnauthenticated, handleClose]);

  return (
    <Dialog
      open={isOpen}
      title="Connect with GitHub"
      description="Link your Diploi account to a GitHub account"
      icon={<GithubLogo color={theme.color.primary[100]} />}
      iconOrnament={<Link color={theme.color.primary[100]} />}
      actions={null}
      onClose={handleClose}
    >
      <div css={contentStyle}>
        <svg width="64" height="64" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8 0C3.58 0 0 3.58 0 8C0 11.54 2.29 14.53 5.47 15.59C5.87 15.66 6.02 15.42 6.02 15.21C6.02 15.02 6.01 14.39 6.01 13.72C4 14.09 3.48 13.23 3.32 12.78C3.23 12.55 2.84 11.84 2.5 11.65C2.22 11.5 1.82 11.13 2.49 11.12C3.12 11.11 3.57 11.7 3.72 11.94C4.44 13.15 5.59 12.81 6.05 12.6C6.12 12.08 6.33 11.73 6.56 11.53C4.78 11.33 2.92 10.64 2.92 7.58C2.92 6.71 3.23 5.99 3.74 5.43C3.66 5.23 3.38 4.41 3.82 3.31C3.82 3.31 4.49 3.1 6.02 4.13C6.66 3.95 7.34 3.86 8.02 3.86C8.7 3.86 9.38 3.95 10.02 4.13C11.55 3.09 12.22 3.31 12.22 3.31C12.66 4.41 12.38 5.23 12.3 5.43C12.81 5.99 13.12 6.7 13.12 7.58C13.12 10.65 11.25 11.33 9.47 11.53C9.76 11.78 10.01 12.26 10.01 13.01C10.01 14.08 10 14.94 10 15.21C10 15.42 10.15 15.67 10.55 15.59C13.71 14.53 16 11.53 16 8C16 3.58 12.42 0 8 0Z"
            transform="scale(64)"
            fill={theme.color.grey[100]}
          />
        </svg>

        {isError && <WarningCircle className="error" weight="fill" size={24} color={theme.color.red[100]} />}

        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2">{description}</Typography>
        <Button
          primary
          icon={isError ? <ArrowClockwise /> : <Plus weight="bold" />}
          loading={isLoading || isAuthenticating}
          onClick={authenticate}
        >
          {button}
        </Button>
        <Help>You might need to enable popups</Help>
      </div>
    </Dialog>
  );
};
